import { useState, useEffect, forwardRef } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Typography,
    Box,
    Paper,
    CircularProgress,
    List,
    Slide
} from '@mui/material'
import { Close, ArrowBack, Download, Visibility, Book } from '@mui/icons-material'
import { manualsRoutes } from '../routes'
import { useStatus } from '../context/Status.context'

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

const UserManualsDialog = ({ open, handleClose, manuals = [] }) => {
    const { setStatus } = useStatus()
    const [selectedManual, setSelectedManual] = useState(null)
    const [pdfUrl, setPdfUrl] = useState(null)
    const [loadingPdf, setLoadingPdf] = useState(false)

    // Reset internal state when dialog closes or opens
    useEffect(() => {
        if (!open) {
            setSelectedManual(null)
            setPdfUrl(null)
            setLoadingPdf(false)
        }
    }, [open])

    const handleDownload = async (manual) => {
        if (!manual || !manual._id) return
        setStatus({
            status: 'loading',
            title: 'Manual de Uso',
            message: 'Obteniendo enlace seguro de descarga...'
        })
        try {
            const res = await manualsRoutes.getManualDownloadUrl(manual._id)
            if (res && res.data && res.data.url) {
                window.open(res.data.url, '_blank')
                setStatus({
                    status: 'completed',
                    title: 'Descarga Iniciada',
                    message: `El manual "${manual.title || 'Manual de Uso'}" se ha abierto/descargado correctamente.`,
                    severity: 'success'
                })
            } else {
                throw new Error('No se pudo obtener el enlace de descarga.')
            }
        } catch (error) {
            console.error('Error downloading manual:', error)
            setStatus({
                status: 'error',
                title: 'Error de Descarga',
                message: error.response?.data?.message || error.message || 'No se pudo descargar el manual.',
                severity: 'error'
            })
        }
    }

    const handleViewPdf = async (manual) => {
        if (!manual || !manual._id) return
        setSelectedManual(manual)
        setLoadingPdf(true)
        setPdfUrl(null)
        try {
            const res = await manualsRoutes.getManualDownloadUrl(manual._id)
            if (res && res.data && res.data.url) {
                setPdfUrl(res.data.url)
            } else {
                alert('No se pudo obtener la URL de visualización del PDF.')
                setSelectedManual(null)
            }
        } catch (error) {
            console.error('Error fetching PDF url for viewing:', error)
            alert('Error al obtener el documento PDF.')
            setSelectedManual(null)
        } finally {
            setLoadingPdf(false)
        }
    }

    const handleBackToList = () => {
        setSelectedManual(null)
        setPdfUrl(null)
        setLoadingPdf(false)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth={selectedManual ? 'lg' : 'sm'}
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, minHeight: selectedManual ? '80vh' : 'auto' }
            }}
        >
            {/* Dialog Header */}
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    {selectedManual ? (
                        <>
                            <IconButton onClick={handleBackToList} size="small" color="primary">
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                {selectedManual.title || 'Visualizando Manual'}
                            </Typography>
                        </>
                    ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Book color="primary" />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    Manuales Disponibles
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent sx={{ p: selectedManual ? 1 : 2.5 }}>
                {selectedManual ? (
                    /* Embedded PDF Viewer Mode */
                    <Box sx={{ width: '100%', height: 'calc(80vh - 120px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {loadingPdf ? (
                            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                <CircularProgress color="primary" />
                                <Typography variant="body2" color="textSecondary">
                                    Cargando visor de documento...
                                </Typography>
                            </Box>
                        ) : pdfUrl ? (
                            <iframe
                                src={pdfUrl}
                                title={selectedManual.title || 'Manual PDF'}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', borderRadius: 8 }}
                            />
                        ) : (
                            <Typography variant="body2" color="error">
                                No se pudo cargar la vista previa del documento PDF.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    /* Available Manuals List Mode */
                    <Box>
                        {manuals && manuals.length > 0 ? (
                            <List sx={{ width: '100%', p: 0 }}>
                                {manuals.map((manual, index) => (
                                    <Paper
                                        key={manual._id || index}
                                        variant="outlined"
                                        sx={{
                                            mb: 1.5,
                                            p: 2,
                                            borderRadius: 2,
                                            transition: 'all 0.2s ease',
                                            '&:hover': { borderColor: '#1976d2', boxShadow: '0 2px 8px rgba(25, 118, 210, 0.12)' }
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Box flex={1} minWidth={200}>
                                                <Typography variant="subtitle1" fontWeight="bold" color="textPrimary">
                                                    {manual.title}
                                                </Typography>
                                                {manual.description && (
                                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                                                        {manual.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box display="flex" gap={1} flexShrink={0}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<Visibility />}
                                                    onClick={() => handleViewPdf(manual)}
                                                    sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 'bold' }}
                                                >
                                                    Visualizar
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<Download />}
                                                    onClick={() => handleDownload(manual)}
                                                    sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 'bold' }}
                                                >
                                                    Descargar
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                                No hay manuales en este momento.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default UserManualsDialog
